import { BREAKPOINTS, isDesktop } from '@theme/utilities';

/**
 * @param {HTMLElement} header
 * @param {boolean} isScrolled
 */
function setStickyScrolled(header, isScrolled) {
  header.dataset.stickyScrolled = isScrolled ? 'true' : 'false';
}

/**
 * @param {HTMLElement} header
 * @param {'up' | 'down' | ''} direction
 */
function setScrollDirection(header, direction) {
  header.dataset.scrollDirection = direction;
}

/** @param {HTMLElement} header */
function getHideOnScrollOffset(header) {
  const offset = Number.parseInt(header.dataset.hideOnScrollOffset ?? '', 10);
  return Number.isFinite(offset) ? offset : 150;
}

/** @param {HTMLElement} header */
function isHeaderStickyAtViewport(header) {
  if (header.dataset.stickyEnabled !== 'true') return false;
  if (header.dataset.stickyDesktopOnly === 'true') return isDesktop();
  return true;
}

/** @param {HTMLElement} header */
function getStickyTopOffset(header) {
  const offset = Number.parseInt(header.dataset.stickyTopOffset ?? '', 10);
  return Number.isFinite(offset) ? offset : 0;
}

/**
 * Document Y at which a transparent Style 1 header should pin.
 * Measured while overlaying (not fixed) so the bar sticks when it
 * reaches the top — after the announcement has scrolled away.
 * @param {HTMLElement} header
 * @returns {number}
 */
function measureTransparentPinY(header) {
  if (header.dataset.stickyScrolled === 'true') {
    const stored = Number.parseFloat(header.dataset.stickyPinY ?? '');
    return Number.isFinite(stored) ? stored : 0;
  }

  const pinY = Math.max(
    0,
    header.getBoundingClientRect().top +
      getScrollTop() -
      getStickyTopOffset(header)
  );
  header.dataset.stickyPinY = String(pinY);
  return pinY;
}

/** @param {HTMLElement} header */
function resetHeaderScrollEffects(header) {
  setStickyScrolled(header, false);
  setScrollDirection(header, '');
  delete header.dataset.stickyPinY;
}

/** @typedef {{ top: number; bottom: number }} HeaderBounds */

/** @returns {number} */
function getScrollTop() {
  return window.pageYOffset || document.documentElement.scrollTop;
}

/**
 * Style 1 — CSS sticky with shadow-on-scroll and hide-on-scroll data attrs.
 */
function initStickyStyle1() {
  const stickyShadowHeaders = document.querySelectorAll(
    '.header_wrapper[data-sticky-enabled="true"][data-sticky-style="style_1"][data-sticky-shadow-on-scroll="true"]'
  );

  const transparentStickyHeaders = document.querySelectorAll(
    '.header_wrapper[data-sticky-enabled="true"][data-sticky-style="style_1"][data-transparent-header="true"]'
  );

  const hideOnScrollHeaders = document.querySelectorAll(
    '.header_wrapper[data-sticky-enabled="true"][data-sticky-style="style_1"][data-hide-on-scroll="true"]'
  );

  const syncStickyScrolled = () => {
    const headers = new Set([
      ...stickyShadowHeaders,
      ...transparentStickyHeaders,
    ]);

    headers.forEach((header) => {
      if (!(header instanceof HTMLElement)) return;

      if (!isHeaderStickyAtViewport(header)) {
        setStickyScrolled(header, false);
        return;
      }

      const scrollTop = getScrollTop();
      const isTransparent = header.dataset.transparentHeader === 'true';
      const pinY = isTransparent ? measureTransparentPinY(header) : 0;
      setStickyScrolled(
        header,
        isTransparent ? scrollTop >= pinY && scrollTop > 0 : scrollTop > 0
      );
    });
  };

  let lastScrollY = window.scrollY;
  let hideScrollTicking = false;
  const scrollThreshold = 10;

  const updateHideOnScrollDirection = () => {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - lastScrollY;

    hideOnScrollHeaders.forEach((header) => {
      if (!(header instanceof HTMLElement)) return;

      if (!isHeaderStickyAtViewport(header)) {
        setScrollDirection(header, '');
        return;
      }

      if (currentScrollY <= 0) {
        setScrollDirection(header, 'up');
        return;
      }

      const hideOffset = getHideOnScrollOffset(header);

      if (currentScrollY <= hideOffset) {
        setScrollDirection(header, 'up');
        return;
      }

      if (Math.abs(scrollDelta) > scrollThreshold) {
        setScrollDirection(header, scrollDelta > 0 ? 'down' : 'up');
      }
    });

    lastScrollY = currentScrollY;
    hideScrollTicking = false;
  };

  const onHideScroll = () => {
    if (!hideScrollTicking) {
      requestAnimationFrame(updateHideOnScrollDirection);
      hideScrollTicking = true;
    }
  };

  const onViewportChange = () => {
    stickyShadowHeaders.forEach((header) => {
      if (header instanceof HTMLElement && !isHeaderStickyAtViewport(header)) {
        resetHeaderScrollEffects(header);
      }
    });

    hideOnScrollHeaders.forEach((header) => {
      if (header instanceof HTMLElement && !isHeaderStickyAtViewport(header)) {
        setScrollDirection(header, '');
      }
    });

    lastScrollY = window.scrollY;
    syncStickyScrolled();

    if (hideOnScrollHeaders.length > 0) {
      updateHideOnScrollDirection();
    }
  };

  if (stickyShadowHeaders.length > 0 || transparentStickyHeaders.length > 0) {
    syncStickyScrolled();
    window.addEventListener('scroll', syncStickyScrolled, { passive: true });
  }

  if (transparentStickyHeaders.length > 0) {
    window.addEventListener(
      'resize',
      () => {
        transparentStickyHeaders.forEach((header) => {
          if (!(header instanceof HTMLElement)) return;
          if (header.dataset.stickyScrolled !== 'true') {
            delete header.dataset.stickyPinY;
          }
        });
        syncStickyScrolled();
      },
      { passive: true }
    );
  }

  if (hideOnScrollHeaders.length > 0) {
    hideOnScrollHeaders.forEach((header) => {
      if (!(header instanceof HTMLElement)) return;

      if (!isHeaderStickyAtViewport(header)) {
        setScrollDirection(header, '');
        return;
      }

      setScrollDirection(header, window.scrollY > 0 ? 'up' : '');
    });

    window.addEventListener('scroll', onHideScroll, { passive: true });
  }

  if (
    stickyShadowHeaders.length > 0 ||
    transparentStickyHeaders.length > 0 ||
    hideOnScrollHeaders.length > 0
  ) {
    const desktopMq = window.matchMedia(
      `(min-width: ${BREAKPOINTS.desktopMin}px)`
    );
    desktopMq.addEventListener('change', onViewportChange);
  }
}

const STYLE_2_FIXED_CLASS = 'header_wrapper--style-2-fixed';
const STYLE_2_HIDDEN_CLASS = 'header_wrapper--style-2-hidden';
const STYLE_2_ANIMATE_CLASS = 'header_wrapper--style-2-animate';

/**
 * Document-space bounds of the header in normal (non-fixed) flow.
 * @param {HTMLElement} headerWrapper
 * @returns {HeaderBounds}
 */
function measureHeaderBounds(headerWrapper) {
  const rect = headerWrapper.getBoundingClientRect();
  const scrollTop = getScrollTop();

  return {
    top: scrollTop + rect.top,
    bottom: scrollTop + rect.bottom,
  };
}

/**
 * Style 2 — header scrolls away in normal flow, then pins with position:fixed.
 *
 * Important: CSS `position: sticky` cannot work here. The header lives inside a
 * short header-group parent; once that group leaves the viewport, sticky is
 * pulled away with it. Fixed positioning is required so hide/reveal stay on screen.
 *
 * @param {HTMLElement} headerWrapper
 */
function initStickyStyle2(headerWrapper) {
  const scrollUpActive = headerWrapper.dataset.showStickyOnScrollUp === 'true';
  const sectionEl = headerWrapper.closest('.shopify-section');

  /** @type {HeaderBounds} */
  let headerBounds = measureHeaderBounds(headerWrapper);
  let currentScrollTop = getScrollTop();
  let preventReveal = false;
  let isAtTop = currentScrollTop <= 0;
  let scrollTicking = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let isScrollingTimeout;

  /** @param {boolean} hidden */
  const setBodyStickyState = (hidden) => {
    document.body.classList.add('shopify-body-section-header-sticky');
    document.body.classList.toggle(
      'shopify-body-section-header-hidden',
      hidden
    );
  };

  const clearBodyStickyState = () => {
    document.body.classList.remove(
      'shopify-body-section-header-hidden',
      'shopify-body-section-header-sticky'
    );
  };

  const isFixed = () => headerWrapper.classList.contains(STYLE_2_FIXED_CLASS);

  const isHidden = () => headerWrapper.classList.contains(STYLE_2_HIDDEN_CLASS);

  const isFixedVisible = () => isFixed() && !isHidden();

  const syncSpacer = () => {
    if (!(sectionEl instanceof HTMLElement)) return;

    if (headerWrapper.dataset.transparentHeader === 'true') {
      sectionEl.style.minHeight = '';
      return;
    }

    if (isFixed()) {
      sectionEl.style.minHeight = `${headerWrapper.offsetHeight}px`;
    } else {
      sectionEl.style.minHeight = '';
    }
  };

  /**
   * Jump to fixed + hidden with no transform transition.
   * If ANIMATE is already on, the browser would paint at translateY(0) then
   * animate to -100% — that is the visible "flash then slide up".
   */
  const pinHiddenWithoutTransition = () => {
    headerWrapper.classList.remove(STYLE_2_ANIMATE_CLASS);
    headerWrapper.classList.add(STYLE_2_FIXED_CLASS, STYLE_2_HIDDEN_CLASS);
    syncSpacer();
    setBodyStickyState(true);
    // Force style flush so the next animate enable does not replay hide
    void headerWrapper.offsetHeight;
    headerWrapper.classList.add(STYLE_2_ANIMATE_CLASS);
  };

  const hide = () => {
    if (isAtTop) return;

    if (!isFixed()) {
      pinHiddenWithoutTransition();
      return;
    }

    headerWrapper.classList.add(
      STYLE_2_FIXED_CLASS,
      STYLE_2_HIDDEN_CLASS,
      STYLE_2_ANIMATE_CLASS
    );
    syncSpacer();
    setBodyStickyState(true);
  };

  const reveal = () => {
    if (isAtTop) return;

    if (!isFixed()) {
      // First pin while already off-screen: land hidden with no flash, then slide in
      pinHiddenWithoutTransition();
      requestAnimationFrame(() => {
        if (isAtTop) return;
        headerWrapper.classList.remove(STYLE_2_HIDDEN_CLASS);
        setBodyStickyState(false);
      });
      return;
    }

    headerWrapper.classList.add(STYLE_2_FIXED_CLASS, STYLE_2_ANIMATE_CLASS);
    headerWrapper.classList.remove(STYLE_2_HIDDEN_CLASS);
    syncSpacer();
    setBodyStickyState(false);
  };

  const reset = () => {
    headerWrapper.classList.remove(
      STYLE_2_FIXED_CLASS,
      STYLE_2_HIDDEN_CLASS,
      STYLE_2_ANIMATE_CLASS
    );
    syncSpacer();
    clearBodyStickyState();
  };

  /**
   * Remeasure only while header is in normal document flow.
   * Measuring while fixed would corrupt headerBounds.
   */
  const refreshHeaderBounds = () => {
    if (isFixed()) return;
    headerBounds = measureHeaderBounds(headerWrapper);
  };

  const updateOnScroll = () => {
    const scrollTop = getScrollTop();
    const scrollingDown = scrollTop > currentScrollTop;
    const scrollingUp = scrollTop < currentScrollTop;
    const pastHeader = scrollTop > headerBounds.bottom;
    const useScrollUpMode = !isDesktop() || scrollUpActive;

    if (scrollTop <= 0) {
      isAtTop = true;
      currentScrollTop = scrollTop;
      reset();
      scrollTicking = false;
      return;
    }

    if (isAtTop && scrollTop > 10) {
      isAtTop = false;
    }

    if (isAtTop) {
      currentScrollTop = scrollTop;
      scrollTicking = false;
      return;
    }

    if (!pastHeader) {
      if (scrollTop <= headerBounds.top || !isFixed()) {
        reset();
      }
      currentScrollTop = scrollTop;
      scrollTicking = false;
      return;
    }

    // Past header — pin and hide/reveal based on mode
    if (useScrollUpMode) {
      if (scrollingDown) {
        hide();
      } else if (scrollingUp) {
        if (!preventReveal) {
          reveal();
        } else {
          window.clearTimeout(isScrollingTimeout);
          isScrollingTimeout = setTimeout(() => {
            preventReveal = false;
          }, 66);
          hide();
        }
      }
    } else if (!isFixedVisible()) {
      // Desktop default: pin immediately once past header — no hide→reveal flash
      reveal();
    }

    currentScrollTop = scrollTop;
    scrollTicking = false;
  };

  const onScroll = () => {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(updateOnScroll);
  };

  const hideHeaderOnScrollUp = () => {
    preventReveal = true;
  };

  refreshHeaderBounds();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      refreshHeaderBounds();
      syncSpacer();
    },
    { passive: true }
  );
  headerWrapper.addEventListener('preventHeaderReveal', hideHeaderOnScrollUp);

  if (currentScrollTop <= 0) {
    reset();
  } else if (currentScrollTop > headerBounds.bottom) {
    if (!isDesktop() || scrollUpActive) {
      hide();
    } else {
      reveal();
    }
  }
}

initStickyStyle1();

document
  .querySelectorAll(
    '.header_wrapper[data-sticky-enabled="true"][data-sticky-style="style_2"]'
  )
  .forEach((headerWrapper) => {
    if (headerWrapper instanceof HTMLElement) {
      initStickyStyle2(headerWrapper);
    }
  });
